import React, { useState, useEffect, useCallback } from 'react';
import { PLANET_DATA } from '../../data/planets';
import ShapeRenderer from './ShapeRenderer';

// ─── Flat question list (all planets combined) ────────────────────────────────
const ALL_QUESTIONS = PLANET_DATA.flatMap((planet) =>
  planet.questions.map((q) => ({
    ...q,
    planetId: planet.id,
    planetName: planet.name,
    planetEmoji: planet.emoji,
    gradFrom: planet.gradFrom,
    gradTo: planet.gradTo,
    glowRgb: planet.glowRgb,
  }))
);

const TOTAL = ALL_QUESTIONS.length;

// Hebrew letters for answer options
const OPTION_LETTERS = ['א', 'ב', 'ג', 'ד'];

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Reuse visual-puzzle + option helpers ─────────────────────────────────────
function VisualPuzzle({ puzzle }) {
  return (
    <div className="sq-visual-puzzle" style={{ marginBottom: '1rem' }}>
      {puzzle.elements.map((el, i) => (
        <div key={i} className="sq-visual-puzzle-item">
          <ShapeRenderer shape={el.shape} color={el.color} size={46} />
        </div>
      ))}
    </div>
  );
}

function OptionContent({ option }) {
  if (typeof option === 'object' && option !== null) {
    return <ShapeRenderer shape={option.shape} color={option.color} size={30} />;
  }
  return option;
}

// ─── Time options ─────────────────────────────────────────────────────────────
const TIME_OPTIONS = [
  { label: 'ללא הגבלה', value: 0 },
  { label: '10 דקות',   value: 10 },
  { label: '15 דקות',   value: 15 },
  { label: '20 דקות',   value: 20 },
  { label: '30 דקות',   value: 30 },
  { label: 'אחר...',    value: 'custom' },
];

// ─── Setup Screen ─────────────────────────────────────────────────────────────
function TestSetup({ onStart, onExit }) {
  const [selection,    setSelection   ] = useState(20);      // preset value or 'custom'
  const [customMins,   setCustomMins  ] = useState('');      // raw input string
  const [customError,  setCustomError ] = useState(false);

  const isCustom = selection === 'custom';

  const handleCustomChange = (e) => {
    setCustomMins(e.target.value);
    setCustomError(false);
  };

  const handleStart = () => {
    if (isCustom) {
      const mins = parseInt(customMins, 10);
      if (!mins || mins < 1 || mins > 180) { setCustomError(true); return; }
      onStart(mins);
    } else {
      onStart(selection);
    }
  };

  // Effective label shown on the start button
  const timeLabel =
    selection === 0       ? 'ללא הגבלה'               :
    isCustom && customMins ? `${customMins} דקות`      :
    isCustom               ? '...'                     :
                             `${selection} דקות`;

  return (
    <div className="sq-test-setup">
      <div className="sq-test-setup-card">
        <button className="sq-test-close-btn" onClick={onExit} aria-label="סגור">✕</button>

        <div className="sq-test-setup-icon" aria-hidden="true">📋</div>
        <h2 className="sq-test-setup-title">מצב מבחן</h2>
        <p className="sq-test-setup-desc">
          <strong>{TOTAL}</strong> שאלות מכל הכוכבים.
          ניתן לדלג על שאלות ולחזור אליהן.
        </p>

        <div className="sq-test-time-section">
          <p className="sq-test-time-label">הגבלת זמן:</p>
          <div className="sq-test-time-grid">
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`sq-test-time-btn${selection === opt.value ? ' selected' : ''}`}
                onClick={() => { setSelection(opt.value); setCustomError(false); }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Custom minutes input */}
          {isCustom && (
            <div className="sq-test-custom-wrap">
              <input
                className={`sq-test-custom-input${customError ? ' error' : ''}`}
                type="number"
                inputMode="numeric"
                min="1"
                max="180"
                placeholder="מספר דקות"
                value={customMins}
                onChange={handleCustomChange}
                autoFocus
              />
              {customError && (
                <span className="sq-test-custom-error">יש להזין מספר בין 1 ל-180</span>
              )}
            </div>
          )}
        </div>

        <button className="sq-test-start-btn" onClick={handleStart}>
          🚀 התחילי מבחן — {timeLabel}
        </button>
      </div>
    </div>
  );
}

// ─── Exam Screen ──────────────────────────────────────────────────────────────
function TestExam({ timeLimit, onSubmit }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(timeLimit > 0 ? timeLimit * 60 : null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  // Auto-submit when time runs out
  useEffect(() => {
    if (secondsLeft === 0) onSubmit(answers, true);
  }, [secondsLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  const question = ALL_QUESTIONS[currentQ];
  const answeredCount = Object.keys(answers).length;

  const selectAnswer = (i) => setAnswers((prev) => ({ ...prev, [currentQ]: i }));
  const clearAnswer = () => setAnswers((prev) => { const n = { ...prev }; delete n[currentQ]; return n; });

  const goPrev = useCallback(() => setCurrentQ((q) => Math.max(0, q - 1)), []);
  const goNext = useCallback(() => setCurrentQ((q) => Math.min(TOTAL - 1, q + 1)), []);

  const handleSubmit = () => {
    if (answeredCount < TOTAL) { setShowConfirm(true); return; }
    onSubmit(answers, false);
  };

  const isWarning  = secondsLeft !== null && secondsLeft < 120;
  const isCritical = secondsLeft !== null && secondsLeft < 30;

  return (
    <div className="sq-test-exam">

      {/* ── Header ── */}
      <div className="sq-test-exam-header">
        <div className="sq-test-meta">
          <span className="sq-test-q-num">שאלה {currentQ + 1} מתוך {TOTAL}</span>
          <span className="sq-test-answered-badge">{answeredCount}/{TOTAL} נענו</span>
        </div>
        {secondsLeft !== null && (
          <div className={`sq-test-timer${isWarning ? ' warning' : ''}${isCritical ? ' critical' : ''}`}>
            ⏱ {formatTime(secondsLeft)}
          </div>
        )}
      </div>

      {/* ── Scrollable body ── */}
      <div className="sq-test-body">

        {/* Planet tag */}
        <div className="sq-test-planet-tag"
          style={{ '--grad-from': question.gradFrom, '--grad-to': question.gradTo }}>
          {question.planetEmoji} {question.planetName}
        </div>

        {/* Question */}
        <div className="sq-test-question-card">
          <p className="sq-test-question-text">{question.text}</p>
          {question.visualPuzzle && <VisualPuzzle puzzle={question.visualPuzzle} />}
        </div>

        {/* Options */}
        <div className="sq-test-options">
          {question.options.map((opt, i) => (
            <button
              key={i}
              className={`sq-test-opt${answers[currentQ] === i ? ' selected' : ''}`}
              onClick={() => selectAnswer(i)}
            >
              <span className="sq-test-opt-letter">{OPTION_LETTERS[i]}</span>
              <span className="sq-test-opt-text"><OptionContent option={opt} /></span>
            </button>
          ))}
        </div>

        {/* Clear / skip hint */}
        <div className="sq-test-hint-row">
          {answers[currentQ] !== undefined
            ? <button className="sq-test-clear-btn" onClick={clearAnswer}>✕ בטלי תשובה</button>
            : <span className="sq-test-skip-hint">💡 ניתן לדלג ולחזור</span>
          }
        </div>
      </div>

      {/* ── Dot navigator ── */}
      <div className="sq-test-dot-nav-wrap">
        <button className="sq-test-nav-btn" onClick={goPrev} disabled={currentQ === 0}
          aria-label="שאלה קודמת">›</button>

        <div className="sq-test-dots" role="list">
          {ALL_QUESTIONS.map((_, i) => (
            <button
              key={i}
              role="listitem"
              aria-label={`שאלה ${i + 1}`}
              className={
                `sq-test-dot` +
                (i === currentQ          ? ' current'  : '') +
                (answers[i] !== undefined ? ' answered' : '')
              }
              onClick={() => setCurrentQ(i)}
            />
          ))}
        </div>

        <button className="sq-test-nav-btn" onClick={goNext} disabled={currentQ === TOTAL - 1}
          aria-label="שאלה הבאה">‹</button>
      </div>

      {/* ── Submit ── */}
      <div className="sq-test-submit-row">
        <button className="sq-test-submit-btn" onClick={handleSubmit}>
          📝 הגישי מבחן ({answeredCount}/{TOTAL})
        </button>
      </div>

      {/* ── Confirm dialog ── */}
      {showConfirm && (
        <div className="sq-test-confirm-overlay">
          <div className="sq-test-confirm-card">
            <p className="sq-test-confirm-msg">
              נשארו <strong>{TOTAL - answeredCount}</strong> שאלות ללא תשובה.<br/>להגיש בכל זאת?
            </p>
            <div className="sq-test-confirm-btns">
              <button className="sq-test-confirm-yes" onClick={() => onSubmit(answers, false)}>כן, הגישי</button>
              <button className="sq-test-confirm-no"  onClick={() => setShowConfirm(false)}>חזרי למבחן</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Results Screen ───────────────────────────────────────────────────────────
function TestResults({ answers, timesUp, onRestart, onExit }) {
  const score     = ALL_QUESTIONS.filter((q, i) => answers[i] === q.correct).length;
  const unanswered = TOTAL - Object.keys(answers).length;
  const pct       = Math.round((score / TOTAL) * 100);

  const praise =
    pct >= 90 ? '🌟 מדהים!' :
    pct >= 75 ? '🎉 כל הכבוד!' :
    pct >= 55 ? '💪 יפה מאוד!' :
                '📚 המשיכי להתאמן!';

  return (
    <div className="sq-test-results">
      <div className="sq-test-results-card">

        {timesUp && <div className="sq-test-timeup-badge">⏰ הזמן נגמר!</div>}

        {/* Score ring */}
        <div className="sq-test-score-ring">
          <span className="sq-test-score-num">{score}</span>
          <span className="sq-test-score-sep">/</span>
          <span className="sq-test-score-total">{TOTAL}</span>
        </div>
        <div className="sq-test-pct">{pct}%</div>
        <h2 className="sq-test-praise">{praise}</h2>

        {unanswered > 0 && (
          <p className="sq-test-unanswered">{unanswered} שאלות לא נענו</p>
        )}

        {/* Per-question grid */}
        <div className="sq-test-breakdown">
          {ALL_QUESTIONS.map((q, i) => {
            const given   = answers[i];
            const correct = given === q.correct;
            const skipped = given === undefined;
            return (
              <div
                key={i}
                className={`sq-test-br-cell${correct ? ' ok' : skipped ? ' skip' : ' err'}`}
                title={`שאלה ${i + 1} — ${q.planetEmoji} ${q.planetName}`}
              >
                <span className="sq-test-br-num">{i + 1}</span>
                <span className="sq-test-br-icon">
                  {skipped ? '—' : correct ? '✓' : '✗'}
                </span>
              </div>
            );
          })}
        </div>

        <div className="sq-test-results-actions">
          <button className="sq-test-action-btn primary" onClick={onRestart}>🔄 מבחן חדש</button>
          <button className="sq-test-action-btn secondary" onClick={onExit}>🗺 חזרי למפה</button>
        </div>
      </div>
    </div>
  );
}

// ─── Root TestMode ────────────────────────────────────────────────────────────
export default function TestMode({ onExit }) {
  const [phase,     setPhase    ] = useState('setup');
  const [timeLimit, setTimeLimit] = useState(20);
  const [answers,   setAnswers  ] = useState({});
  const [timesUp,   setTimesUp  ] = useState(false);

  const handleStart = (limit) => {
    setTimeLimit(limit);
    setAnswers({});
    setTimesUp(false);
    setPhase('exam');
  };

  const handleSubmit = (finalAnswers, wasTimesUp) => {
    setAnswers(finalAnswers);
    setTimesUp(wasTimesUp);
    setPhase('results');
  };

  if (phase === 'setup' ) return <TestSetup   onStart={handleStart}  onExit={onExit} />;
  if (phase === 'exam'  ) return <TestExam    timeLimit={timeLimit}  onSubmit={handleSubmit} />;
  if (phase === 'results') return <TestResults answers={answers}     timesUp={timesUp}
                                               onRestart={() => setPhase('setup')} onExit={onExit} />;
  return null;
}
